'use client'

import {
  getListPagination,
  useProfile,
  useRetryFitAnalysis,
  PaneListSkeleton,
  PaneResponsiveCardGrid,
  PaneListEmptyState,
  Card,
  CardContent,
  useArtifactCanvas,
  sortShortlistWorkbenchItems,
  buildSavedOpportunityFitProps,
  ListPagination,
  readListPage,
  writeListPage,
} from '@yeshuman/sdk'
import { useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Bookmark } from 'lucide-react'
import { buildApplicationStatusMap, useMyApplications } from '../../hooks/use-my-applications'
import { useSavedOpportunities } from '../../hooks/use-saved-opportunities'
import type { SavedOpportunity } from '../../hooks/use-saved-opportunities'
import type { Opportunity } from '../../hooks/use-opportunities'
import { CandidateOpportunityJobCard } from './candidate-opportunity-job-card'
import { AnalysisRetryButton } from '../search/analysis-retry-button'
import { openApplicationApplyCanvas } from './open-application-apply-canvas'
import { useNavigateToCandidateJobDetail } from '../../hooks/use-navigate-to-candidate-job-detail'
import { hasActiveApplicationForOpportunity } from './job-application-trailing'

const emptySavedState = (
  <Card className="w-full">
    <CardContent>
      <PaneListEmptyState
        icon={<Bookmark aria-hidden />}
        title="No saved jobs yet"
        description="Save jobs you're considering so you can apply later and check your fit."
      />
    </CardContent>
  </Card>
)

export function SavedOpportunitiesList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const listPagination = getListPagination('candidateShortlist')
  const page = readListPage(searchParams)
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const applyCanvasKeyRef = useRef(0)
  const handleOpenJob = useNavigateToCandidateJobDetail({ fromShortlist: true })
  const { profile } = useProfile()
  const {
    savedOpportunities,
    savedIds,
    savedAtByOpportunityId,
    pagination,
    pendingOpportunityIds,
    stuckOpportunityIds,
    isLoading,
    error,
    toggleSave,
    savingOpportunityId,
    refetch,
  } = useSavedOpportunities({
    page: listPagination.enabled ? page : undefined,
    pageSize: listPagination.pageSize,
  })
  const { retryCandidate, retryCandidateList, mutatingOpportunityId, isListPending } =
    useRetryFitAnalysis()

  const { applications, refetch: refetchApplications } = useMyApplications({
    enabled: !!profile?.id,
  })

  const applicationStatusMap = useMemo(
    () => buildApplicationStatusMap(applications),
    [applications],
  )

  // Applied and closed jobs leave the Saved view; their rows are kept in data.
  const visibleItems = useMemo(() => {
    const rows = savedOpportunities
      .filter(
        (opp) =>
          opp.is_available &&
          !hasActiveApplicationForOpportunity(applicationStatusMap, opp.id),
      )
      .map((opp) => ({
        ...opp,
        savedAt: savedAtByOpportunityId[opp.id] ?? opp.updated_at,
      }))

    return sortShortlistWorkbenchItems(rows)
  }, [applicationStatusMap, savedAtByOpportunityId, savedOpportunities])

  const rerunJobIds = useMemo(
    () => visibleItems.filter((job) => !pendingOpportunityIds.has(job.id)).map((job) => job.id),
    [pendingOpportunityIds, visibleItems],
  )

  const handleSaveToggle = useCallback(
    (opportunityId: string) => {
      toggleSave(opportunityId, savedIds.has(opportunityId))
    },
    [savedIds, toggleSave],
  )

  const handleApplyClick = useCallback(
    (opportunity: Opportunity) => {
      // Marketplace apply is off-platform (or alpha-captured) on the detail page — not ATS canvas.
      if (opportunity.kind === 'marketplace') {
        handleOpenJob(opportunity)
        return
      }
      openApplicationApplyCanvas({
        opportunity: opportunity as SavedOpportunity,
        openCanvas,
        closeCanvas,
        applyCanvasKeyRef,
        onApplied: () => {
          void refetchApplications()
          void refetch()
        },
        keyPrefix: 'saved-apply',
      })
    },
    [closeCanvas, handleOpenJob, openCanvas, refetch, refetchApplications],
  )

  if (isLoading) {
    return <PaneListSkeleton count={4} ariaLabel="Loading saved jobs" />
  }

  if (error) {
    return (
      <Card className="w-full">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <h3 className="mb-2 text-lg font-medium text-destructive">Error loading saved jobs</h3>
          <p className="text-center text-muted-foreground">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </CardContent>
      </Card>
    )
  }

  const paginationFooter =
    listPagination.enabled && pagination ? (
      <ListPagination
        page={pagination.page}
        pageSize={pagination.page_size}
        total={pagination.total}
        onPageChange={(nextPage) => {
          setSearchParams(writeListPage(searchParams, nextPage), { replace: true })
        }}
      />
    ) : null

  if (savedOpportunities.length === 0) {
    if ((pagination?.total ?? 0) > 0) {
      return (
        <div className="space-y-4">
          <Card className="w-full">
            <CardContent>
              <PaneListEmptyState
                icon={<Bookmark aria-hidden />}
                title="No saved jobs on this page"
                description="Try a previous page — your saved jobs are still here."
              />
            </CardContent>
          </Card>
          {paginationFooter}
        </div>
      )
    }
    return emptySavedState
  }

  if (visibleItems.length === 0) {
    return (
      <div className="space-y-4">
        <Card className="w-full">
          <CardContent>
            <PaneListEmptyState
              icon={<Bookmark aria-hidden />}
              title="No saved jobs right now"
              description="Applied jobs move to Applied, and closed listings are hidden automatically. Withdraw an application to see a job here again."
            />
          </CardContent>
        </Card>
        {paginationFooter}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <AnalysisRetryButton
          mode="run"
          ariaLabel="Re-run analysis for your saved jobs"
          disabled={rerunJobIds.length === 0}
          pending={isListPending}
          onClick={() => retryCandidateList(rerunJobIds)}
        />
      </div>
      <PaneResponsiveCardGrid>
        {visibleItems.map((opportunity) => (
          <CandidateOpportunityJobCard
            key={opportunity.id}
            className="h-full"
            opportunity={opportunity}
            application={applicationStatusMap[opportunity.id]}
            saved
            saveMode="always"
            isSaving={savingOpportunityId === opportunity.id}
            fitProps={buildSavedOpportunityFitProps(opportunity, {
              pending: pendingOpportunityIds.has(opportunity.id),
              stuck: stuckOpportunityIds.has(opportunity.id),
              onRetry: () => retryCandidate(opportunity.id),
              retryPending: mutatingOpportunityId === opportunity.id,
            })}
            onOpenJob={handleOpenJob}
            onSaveToggle={handleSaveToggle}
            onApply={handleApplyClick}
          />
        ))}
      </PaneResponsiveCardGrid>
      {paginationFooter}
    </div>
  )
}
